// Client error/warning capture — the browser half of the platform-wide logger.
//
// TYPESCRIPT COPY. This is the same module the four Vite apps carry as
// `clientErrorLogger.js`, ported to TypeScript because this project sets
// `allowJs: false` and `strict: true`. The behaviour is identical; only the
// annotations differ. Keep the two in step.
//
// Hooks window's `error` and `unhandledrejection` events and patches
// console.error / console.warn, then batches what it sees to
// POST <apiBase>/client-errors on :4001, where lib/clientErrorLog.js groups it
// by fingerprint. The internal dashboard's "Client Errors" page triages the
// result. See mongo-api/src/routes/clientErrors.js.
//
// ---------------------------------------------------------------------------
// THE LOGGER MUST NEVER BECOME THE PROBLEM. This file runs inside a POS till
// taking real money. Every rule below exists because the obvious implementation
// breaks one of them:
//
//   NO RECURSION.   console.error is patched, so anything this file logs to the
//                   console would re-enter capture(). `capturing` short-circuits
//                   re-entry, and this file never calls console.* itself.
//   NO THROWING.    Every entry point is wrapped. A logger that throws inside
//                   window.onerror turns one bug into an infinite loop.
//   NO BLOCKING.    Capture is synchronous but does nothing except push onto an
//                   array. The send is a debounced, un-awaited fetch.
//   NO SPAM.        Three independent limits: per-fingerprint dedupe (identical
//                   events collapse to a count), a sliding per-minute rate cap,
//                   and a per-session cap on distinct fingerprints.
//   NO SELF-HARM.   If the API is unreachable the queue is DROPPED, never
//                   retried and never grown. Three consecutive transport
//                   failures open a circuit breaker that silences the logger for
//                   five minutes.
//   NO SECRETS.     scrub() strips tokens, keys, passwords, emails and long
//                   digit runs (phone numbers, card PANs) before anything leaves
//                   the tab. The server redacts again — this side is a courtesy,
//                   not the guarantee.
// ---------------------------------------------------------------------------

const LIMITS = {
  // Flush when either fires.
  FLUSH_DEBOUNCE_MS: 4000,
  FLUSH_AT_QUEUE: 10,
  // Sliding window: at most this many events accepted per minute, per tab.
  RATE_WINDOW_MS: 60000,
  RATE_MAX_EVENTS: 25,
  // A tab that produces more than this many DISTINCT problems is broken in a way
  // no amount of extra rows will clarify.
  MAX_FINGERPRINTS: 60,
  // Identical event inside this window increments a counter instead of queuing.
  DEDUPE_WINDOW_MS: 60000,
  // Payload caps, mirrored server-side.
  MAX_MESSAGE: 600,
  MAX_STACK: 3000,
  MAX_URL: 500,
  MAX_BATCH: 20,
  // Circuit breaker.
  MAX_TRANSPORT_FAILURES: 3,
  BREAKER_MS: 5 * 60 * 1000,
};

// Chatty, information-free browser noise. Kept deliberately short: the point of
// this system is to RECORD warnings rather than silence them, so nothing that
// names our own code is ignored. (The recovered POS's React Router
// `v7_startTransition` / `v7_relativeSplatPath` warnings are exactly the kind of
// thing that must show up here — main.jsx documents that enabling
// v7_startTransition caused a real user-visible bug, so the warning is a
// standing decision to review, not noise to mute.)
const IGNORE = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
];

const SCRUB: Array<[RegExp, string]> = [
  [/\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi, "Bearer [redacted]"],
  [/\beyJ[A-Za-z0-9._-]{10,}/g, "[redacted-jwt]"],
  [/\b(?:AIza|ya29\.)[A-Za-z0-9._-]{10,}/g, "[redacted-key]"],
  [/([?&#])(token|access_token|id_token|auth|authorization|api_key|apikey|key|secret|password|pass|pwd|otp|code|signature|sig|session)=[^&#\s"']*/gi, "$1$2=[redacted]"],
  [/("|')?(token|password|passwd|pwd|secret|api_?key|authorization|otp)("|')?\s*[:=]\s*("[^"]*"|'[^']*'|[^\s,;}&#]+)/gi, "$2=[redacted]"],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[redacted-email]"],
  [/\d{9,}/g, "[redacted-number]"],
];

type Severity = "error" | "warning";
type Platform = "ADMIN" | "POS" | "WAREHOUSE" | "WEB" | "INTERNAL";

export interface ClientErrorContext {
  tenant_id?: string | null;
  user_id?: string | null;
  user_name?: string | null;
  user_role?: string | null;
}

export interface ClientErrorLoggerOptions {
  platform: Platform;
  endpoint: string;
  getContext?: () => ClientErrorContext | null | undefined;
  appVersion?: string | null;
  enabled?: boolean;
}

interface QueuedEvent extends ClientErrorContext {
  platform: Platform;
  severity: Severity;
  source: string;
  message: string;
  stack: string;
  url: string;
  route: string;
  user_agent: string;
  at: string;
  count: number;
}

function scrub(value: unknown, max: number): string {
  try {
    if (value == null) return "";
    let out = String(value);
    for (let i = 0; i < SCRUB.length; i += 1) out = out.replace(SCRUB[i][0], SCRUB[i][1]);
    return out.length > max ? out.slice(0, max) : out;
  } catch {
    return "";
  }
}

/**
 * Bounded object -> string. NOT JSON.stringify.
 *
 * JSON.stringify walks the ENTIRE object graph before any truncation can be
 * applied, so `console.error(someHugeThing)` would serialise megabytes and only
 * then have 400 characters kept — on the main thread, inside a console call.
 * A replacer that shortens strings does not help: the walk is the cost. This
 * walker stops at a depth, a key count and a total budget, so the work is
 * bounded no matter what it is handed.
 */
function describe(value: unknown, depth: number, budget: { left: number }): string {
  if (budget.left <= 0) return "…";
  if (value === null) return "null";
  const t = typeof value;
  if (t === "string") {
    const str = value as string;
    const short = str.length > 120 ? `${str.slice(0, 120)}…` : str;
    budget.left -= short.length;
    return JSON.stringify(short);
  }
  if (t === "number" || t === "boolean" || t === "undefined" || t === "bigint") {
    budget.left -= 6;
    return String(value);
  }
  if (t === "function") return `[function ${(value as { name?: string }).name || "anonymous"}]`;
  if (t === "symbol") return "[symbol]";
  if (value instanceof Error) return `${value.name || "Error"}: ${value.message}`;
  if (value instanceof Date) return value.toISOString();
  // Things whose graphs are enormous and whose contents are never the point.
  if (typeof Node !== "undefined" && value instanceof Node) return `[${value.nodeName}]`;
  const obj = value as Record<string, unknown>;
  if (obj.$$typeof) return "[ReactElement]";
  if (obj._owner || obj.stateNode) return "[ReactFiber]";
  if (depth <= 0) return Array.isArray(value) ? "[Array]" : "[Object]";
  try {
    if (Array.isArray(value)) {
      const items: string[] = [];
      for (let i = 0; i < value.length && i < 10 && budget.left > 0; i += 1) {
        items.push(describe(value[i], depth - 1, budget));
      }
      if (value.length > items.length) items.push(`…+${value.length - items.length}`);
      return `[${items.join(",")}]`;
    }
    const keys = Object.keys(obj);
    const parts: string[] = [];
    for (let i = 0; i < keys.length && i < 15 && budget.left > 0; i += 1) {
      budget.left -= keys[i].length + 2;
      // Per KEY, not per object: one throwing getter (or a Proxy trap) would
      // otherwise discard every readable field beside it.
      let rendered: string;
      try {
        rendered = describe(obj[keys[i]], depth - 1, budget);
      } catch {
        rendered = "[unreadable]";
      }
      parts.push(`${keys[i]}:${rendered}`);
    }
    if (keys.length > parts.length) parts.push(`…+${keys.length - parts.length}`);
    return `{${parts.join(",")}}`;
  } catch {
    return "[unreadable]";
  }
}

/**
 * Turn console.* arguments into one string, with hard limits and no unbounded
 * traversal. Errors keep their message; DOM nodes and React elements are named,
 * not dumped — walking a React element means walking its props, which on this
 * platform means walking an order.
 */
function formatArgs(args: unknown[]): string {
  const parts: string[] = [];
  for (let i = 0; i < args.length && i < 8; i += 1) {
    const a = args[i];
    try {
      if (typeof a === "string") parts.push(a.length > 400 ? `${a.slice(0, 400)}…` : a);
      else if (a instanceof Error) parts.push(`${a.name || "Error"}: ${a.message}`);
      else parts.push(describe(a, 3, { left: 400 }));
    } catch {
      parts.push("[unserialisable]");
    }
  }
  return parts.join(" ");
}

/** The first Error in a console.* argument list, for its stack. */
function firstErrorStack(args: unknown[]): string {
  for (let i = 0; i < args.length; i += 1) {
    const a = args[i];
    if (a instanceof Error && a.stack) return String(a.stack);
  }
  return "";
}

const state: {
  installed: boolean;
  capturing: boolean;
  queue: QueuedEvent[];
  seen: Map<string, { at: number; index: number }>;
  windowStart: number;
  windowCount: number;
  timer: ReturnType<typeof setTimeout> | null;
  failures: number;
  mutedUntil: number;
  options: ClientErrorLoggerOptions | null;
} = {
  installed: false,
  capturing: false, // re-entry guard — see NO RECURSION above
  queue: [],
  seen: new Map(),
  windowStart: 0,
  windowCount: 0,
  timer: null,
  failures: 0,
  mutedUntil: 0,
  options: null,
};

function localFingerprint(e: QueuedEvent): string {
  return [e.platform, e.severity, e.message, (e.stack || "").split("\n")[1] || "", e.route].join("|");
}

function currentUrl(): string {
  try {
    const u = typeof location !== "undefined" ? location.href : "";
    // Redact query values the same way the server will, so nothing sensitive is
    // even put on the wire.
    return scrub(u, LIMITS.MAX_URL);
  } catch {
    return "";
  }
}

function currentRoute(): string {
  try {
    return typeof location !== "undefined" ? location.pathname : "";
  } catch {
    return "";
  }
}

function send(batch: QueuedEvent[]): void {
  const opts = state.options;
  if (!opts) return;
  const { endpoint, platform, appVersion } = opts;
  const body = JSON.stringify({ platform, app_version: appVersion || null, events: batch });
  try {
    // keepalive so a batch queued during unload still leaves the tab.
    // credentials omitted: this endpoint needs no session and must never carry
    // one. No retry — a failed batch is dropped, by design.
    const p = fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
      cache: "no-store",
    });
    if (p && typeof p.then === "function") {
      p.then(
        (res) => {
          if (res && res.ok) state.failures = 0;
          else bumpFailure();
        },
        () => bumpFailure(),
      );
    }
  } catch {
    bumpFailure();
  }
}

function bumpFailure(): void {
  state.failures += 1;
  if (state.failures >= LIMITS.MAX_TRANSPORT_FAILURES) {
    // The API is unreachable. Stop trying, drop what is queued, and come back in
    // five minutes. A logger that keeps firing failing requests at a dead API is
    // a second outage layered on the first.
    state.mutedUntil = Date.now() + LIMITS.BREAKER_MS;
    state.failures = 0;
    state.queue.length = 0;
  }
}

function flush(): void {
  try {
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }
    if (!state.queue.length) return;
    if (Date.now() < state.mutedUntil) {
      state.queue.length = 0;
      return;
    }
    const batch = state.queue.splice(0, LIMITS.MAX_BATCH);
    // Anything beyond one batch is dropped rather than carried: an unbounded
    // client-side buffer is a memory leak on a till that runs for days.
    state.queue.length = 0;
    // The fingerprint map is NOT cleared. Its entries survive the flush with
    // `index = -1`, meaning "sent, do not re-queue until the dedupe window
    // expires". Clearing it here instead would let a permanently-repeating error
    // (a render loop) re-queue itself on every single flush, so the API would
    // take a request every few seconds from every affected till for as long as
    // the bug lived. Keeping it caps that at one request per fingerprint per
    // minute per tab.
    state.seen.forEach((v) => { v.index = -1; });
    send(batch);
  } catch {
    /* never throw out of a flush */
  }
}

function schedule(): void {
  if (state.timer) return;
  try {
    state.timer = setTimeout(flush, LIMITS.FLUSH_DEBOUNCE_MS);
  } catch {
    /* no timers available (SSR) — nothing to schedule */
  }
}

function capture(severity: Severity, source: string, message: string, stack?: string): void {
  if (state.capturing || !state.options) return;
  state.capturing = true;
  try {
    const now = Date.now();
    if (now < state.mutedUntil) return;

    const msg = scrub(message, LIMITS.MAX_MESSAGE);
    if (!msg) return;
    for (let i = 0; i < IGNORE.length; i += 1) if (msg.indexOf(IGNORE[i]) !== -1) return;
    // Never report our own ingest failing — that is the feedback loop.
    if (msg.indexOf("/client-errors") !== -1) return;

    const evt: QueuedEvent = {
      platform: state.options.platform,
      severity,
      source,
      message: msg,
      stack: scrub(stack, LIMITS.MAX_STACK),
      url: currentUrl(),
      route: currentRoute(),
      user_agent: typeof navigator !== "undefined" ? String(navigator.userAgent || "").slice(0, 300) : "",
      at: new Date(now).toISOString(),
      count: 1,
    };

    // Per-app identity. getContext is supplied by the host app and may read
    // Redux, a token store, anything — so it is treated as hostile: wrapped, and
    // only the four fields below are taken from whatever it returns.
    try {
      const ctx = state.options.getContext ? state.options.getContext() : null;
      if (ctx) {
        evt.tenant_id = ctx.tenant_id ? String(ctx.tenant_id).slice(0, 80) : null;
        evt.user_id = ctx.user_id ? String(ctx.user_id).slice(0, 80) : null;
        evt.user_name = ctx.user_name ? scrub(ctx.user_name, 120) : null;
        evt.user_role = ctx.user_role ? String(ctx.user_role).slice(0, 80) : null;
      }
    } catch {
      /* an app whose store is mid-teardown must still get its error logged */
    }

    // 1) Dedupe. The same event inside the window either increments the copy
    //    that is still queued, or — if that copy has already been sent — is
    //    dropped entirely. The server keeps its own `count` per group, so a
    //    dropped repeat is not a lost fact, just one that was already reported.
    const print = localFingerprint(evt);
    const prior = state.seen.get(print);
    if (prior && now - prior.at < LIMITS.DEDUPE_WINDOW_MS) {
      prior.at = now;
      if (prior.index >= 0 && state.queue[prior.index]) state.queue[prior.index].count += 1;
      return;
    }
    if (prior) state.seen.delete(print); // window expired — treat as new

    // 2) Distinct-problem cap, with a prune of expired entries first so a long
    //    session cannot silently reach the cap and stop reporting forever.
    if (state.seen.size >= LIMITS.MAX_FINGERPRINTS) {
      state.seen.forEach((v, k) => {
        if (now - v.at >= LIMITS.DEDUPE_WINDOW_MS) state.seen.delete(k);
      });
      if (state.seen.size >= LIMITS.MAX_FINGERPRINTS) return;
    }
    // 3) Sliding rate window.
    if (now - state.windowStart > LIMITS.RATE_WINDOW_MS) {
      state.windowStart = now;
      state.windowCount = 0;
    }
    state.windowCount += 1;
    if (state.windowCount > LIMITS.RATE_MAX_EVENTS) return;

    state.seen.set(print, { at: now, index: state.queue.length });
    state.queue.push(evt);
    if (state.queue.length >= LIMITS.FLUSH_AT_QUEUE) flush();
    else schedule();
  } catch {
    /* capture must never throw — it runs inside window.onerror */
  } finally {
    state.capturing = false;
  }
}

/**
 * Install the capture hooks. Idempotent, browser-only, and a no-op when
 * `enabled` is false.
 *
 *   platform   "ADMIN" | "POS" | "WAREHOUSE" | "WEB" | "INTERNAL"
 *   endpoint   absolute or same-origin URL of POST /client-errors
 *   getContext () => { tenant_id, user_id, user_name, user_role } — called on
 *              every capture, wrapped in try/catch. MUST NOT return a phone
 *              number, email, token or any customer record: see the privacy note
 *              in mongo-api/src/lib/clientErrorLog.js.
 *   appVersion optional build tag
 */
export function installClientErrorLogger(options: ClientErrorLoggerOptions): void {
  try {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (state.installed) return;
    if (options && options.enabled === false) return;
    if (!options || !options.endpoint || !options.platform) return;
    state.installed = true;
    state.options = options;

    window.addEventListener(
      "error",
      (event) => {
        try {
          // Resource-load failures (a missing <img>) surface on this same event
          // with a target and no error. They are noise here and would drown the
          // real script errors, so they are skipped.
          if (!event || (!event.message && !event.error)) return;
          const err = event.error;
          const msg = err && err.message ? `${err.name || "Error"}: ${err.message}` : String(event.message || "");
          const where = event.filename ? ` (${event.filename}:${event.lineno}:${event.colno})` : "";
          capture("error", "window.onerror", msg + where, err && err.stack);
        } catch {
          /* no-op */
        }
      },
      true,
    );

    window.addEventListener("unhandledrejection", (event) => {
      try {
        const reason = event && event.reason;
        let msg;
        let stack = "";
        if (reason instanceof Error) {
          msg = `${reason.name || "Error"}: ${reason.message}`;
          stack = reason.stack || "";
        } else {
          msg = `Unhandled rejection: ${formatArgs([reason])}`;
        }
        capture("error", "unhandledrejection", msg, stack);
      } catch {
        /* no-op */
      }
    });

    // console.error / console.warn. The ORIGINAL is called first and its return
    // value is preserved, so the developer console behaves exactly as before —
    // this is a tap, not a replacement.
    const patch = (method: "error" | "warn", severity: Severity) => {
      const original = console[method];
      if (typeof original !== "function") return;
      const target = console as unknown as Record<string, unknown>;
      target[method] = function patched(...args: unknown[]) {
        let out: unknown;
        try {
          out = original.apply(console, args);
        } finally {
          try {
            capture(severity, `console.${method}`, formatArgs(args), firstErrorStack(args));
          } catch {
            /* no-op */
          }
        }
        return out;
      };
      (target[method] as { __eposmaticPatched?: boolean }).__eposmaticPatched = true;
    };
    patch("error", "error");
    patch("warn", "warning");

    // Get the last batch out before the tab goes away. `pagehide` fires on
    // mobile Safari where `beforeunload` does not; `visibilitychange` covers a
    // backgrounded till that is never actually closed.
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush();
    });
  } catch {
    /* installation failing must not stop the app booting */
  }
}

/** Escape hatch for a deliberate report from a caught error. Never throws. */
export function reportClientError(message: string, stack?: string, severity?: Severity): void {
  capture(severity === "warning" ? "warning" : "error", "manual", String(message || ""), stack || "");
}
